/*
  Ce spune comunitatea: tabs (snippets/dp-pdp-community.liquid, styles in main-product.css).
  - ARIA tabs: click, Left / Right arrows (wrap), Home / End, roving tabindex, inactive panels hidden.
  - Mobile: the tab row scrolls sideways, with a fade on the side that has more tabs (is-l / is-r).
  - Touch: a sideways swipe on the panels opens the next / previous tab (no wrap at the first / last one), the same as
    a tap on it. The panel follows the finger with its neighbour coming in beside it, then slides into place; with
    reduced motion the tab just changes. Vertical moves stay a normal page scroll (touch-action: pan-y in the CSS).
    The mouse is left alone, so text can still be selected on desktop.
  - No jumping (min-height on the panel box):
    wide block (640px and up, same breakpoint as the CSS): the box never shrinks while the visitor switches tabs,
    it only grows the first time a taller tab opens, so the page below doesn't bounce up and down;
    narrow block: the box never gets shorter than the first panel, taller tabs grow it and it comes back to that.
    In both cases the tab row stays where it was on screen.
  A custom element, so it starts by itself wherever the block is inserted (several blocks per page, drawers, theme editor).
*/
(() => {
  if (!window.customElements || customElements.get('dp-community-tabs')) return;

  const WIDE = 640;
  const SWIPE = 50; // px sideways before a swipe changes the tab
  const GAP = 32; // px between the open panel and the one coming in
  const SLIDE = 240; // ms
  let count = 0;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class DpCommunityTabs extends HTMLElement {
    connectedCallback() {
      if (this.ac) return;
      this.list = this.querySelector('[role="tablist"]');
      this.box = this.querySelector('.dp-ct__panels');
      if (!this.list || !this.box) return;
      this.tabs = [...this.list.querySelectorAll('[role="tab"]')];
      if (!this.tabs.length) return;
      this.panels = this.tabs.map((t) => this.box.querySelector(`[id="${t.getAttribute('aria-controls')}"]`));
      this.uniqueIds();

      this.ac = new AbortController();
      const { signal } = this.ac;

      this.list.addEventListener('click', (e) => {
        const tab = e.target.closest('[role="tab"]');
        if (tab) this.select(this.tabs.indexOf(tab), false);
      }, { signal });

      this.list.addEventListener('keydown', (e) => {
        const i = this.tabs.indexOf(e.target);
        if (i < 0) return;
        const n = this.tabs.length;
        const next = { ArrowRight: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, Home: 0, End: n - 1 }[e.key];
        if (next === undefined) return;
        e.preventDefault();
        this.select(next, true);
      }, { signal });

      // Swipe on the panels: touch and pen only
      this.box.addEventListener('pointerdown', (e) => this.press(e), { signal, passive: true });
      this.box.addEventListener('pointermove', (e) => this.drag(e), { signal, passive: true });
      this.box.addEventListener('pointerup', (e) => this.release(e, false), { signal, passive: true });
      this.box.addEventListener('pointercancel', (e) => this.release(e, true), { signal, passive: true });

      this.list.addEventListener('scroll', () => this.edges(), { signal, passive: true });

      this.ro = new ResizeObserver(() => {
        this.edges();
        this.fit();
      });
      this.ro.observe(this.list);
      this.ro.observe(this.box);

      document.fonts?.ready.then(() => {
        if (!this.ac) return;
        this.w = null;
        this.edges();
        this.fit();
      });

      this.edges();
      this.fit();
    }

    disconnectedCallback() {
      this.ac?.abort();
      this.ro?.disconnect();
      this.ac = this.ro = this.w = this.swipe = null;
    }

    // The same product can render twice on a page (page + quick view): the second copy gets its own ids.
    uniqueIds() {
      if (document.getElementById(this.tabs[0].id) === this.tabs[0]) return;
      const suffix = `-${++count}`;
      this.tabs.forEach((t, i) => {
        const p = this.panels[i];
        t.id += suffix;
        if (!p) return;
        p.id += suffix;
        t.setAttribute('aria-controls', p.id);
        p.setAttribute('aria-labelledby', t.id);
      });
    }

    select(i, focus) {
      const tab = this.tabs[i];
      if (!tab) return;
      const before = this.list.getBoundingClientRect().top;
      const height = this.box.offsetHeight;

      this.tabs.forEach((t, j) => {
        const on = i === j;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (this.panels[j]) this.panels[j].hidden = !on;
      });
      if (this.wide) this.box.style.minHeight = `${height}px`;
      if (focus) tab.focus({ preventScroll: true });

      // If the browser moved the page anyway (scroll anchoring, end of page), put the tab row back where it was
      const moved = this.list.getBoundingClientRect().top - before;
      if (Math.abs(moved) >= 1) window.scrollBy(0, moved);

      // Bring the chosen tab into view inside the row
      const list = this.list;
      const max = list.scrollWidth - list.clientWidth;
      if (max > 1) {
        const t = tab.getBoundingClientRect();
        const l = list.getBoundingClientRect();
        const target = list.scrollLeft + (t.left - l.left) - (list.clientWidth - t.width) / 2;
        list.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
      this.edges();
    }

    // Fades on the edges of the tab row, only on the side that has more tabs
    edges() {
      const list = this.list;
      const max = list.scrollWidth - list.clientWidth;
      list.classList.toggle('is-l', max > 2 && list.scrollLeft > 2);
      list.classList.toggle('is-r', max > 2 && list.scrollLeft < max - 2);
    }

    // Runs when the block width changes: start again from the open panel's own height (wide),
    // or from the first panel's height (narrow).
    fit() {
      const w = this.clientWidth;
      if (w === this.w) return;
      this.w = w;
      this.wide = w >= WIDE;
      const box = this.box;
      box.style.minHeight = '';
      if (this.wide || this.panels.length < 2) return;

      const p = this.panels[0];
      if (!p) return;
      let h;
      if (p.hidden) {
        p.hidden = false;
        p.style.cssText += ';position:absolute;visibility:hidden;inset-inline:0';
        h = p.offsetHeight;
        p.style.removeProperty('position');
        p.style.removeProperty('visibility');
        p.style.removeProperty('inset-inline');
        p.hidden = true;
      } else {
        h = p.offsetHeight;
      }
      if (h) box.style.minHeight = `${h}px`;
    }

    // A swipe starts: touch or pen (the mouse keeps selecting text), one finger, not while the last one is still
    // sliding, and not on something inside a panel that scrolls sideways by itself.
    press(e) {
      if (e.pointerType === 'mouse' || !e.isPrimary || this.swipe || this.sliding || this.tabs.length < 2) return;
      if (this.scrolls(e.target)) return;
      this.swipe = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, dy: 0, on: false, peek: -1 };
    }

    // The direction is decided after a few pixels. Mostly vertical is a page scroll and is left to the browser
    // (it also sends pointercancel once it scrolls); mostly sideways is a swipe and the panels follow the finger.
    drag(e) {
      const s = this.swipe;
      if (!s || e.pointerId !== s.id) return;
      s.dx = e.clientX - s.x;
      s.dy = e.clientY - s.y;
      if (!s.on) {
        if (Math.abs(s.dx) < 10 && Math.abs(s.dy) < 10) return;
        s.i = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        if (Math.abs(s.dx) <= Math.abs(s.dy) || !this.panels[s.i]) {
          this.swipe = null;
          return;
        }
        s.on = true;
        s.anim = !reduceMotion.matches;
        s.w = this.box.clientWidth + GAP;
        if (s.anim) this.box.classList.add('is-drag');
      }
      if (s.anim) this.follow(s);
    }

    // Finger up: far enough (SWIPE) and clearly sideways (1.5 times the vertical move) opens the neighbour tab through
    // select(), exactly like a tap. Anything else, or a cancel, puts the panels back where they were.
    release(e, cancel) {
      const s = this.swipe;
      if (!s || e.pointerId !== s.id) return;
      this.swipe = null;
      if (!s.on) return;
      if (!cancel) {
        s.dx = e.clientX - s.x;
        s.dy = e.clientY - s.y;
      }
      const dir = s.dx < 0 ? 1 : -1;
      const j = s.i + dir;
      const go = !cancel && !!this.panels[j] && Math.abs(s.dx) > SWIPE && Math.abs(s.dx) > 1.5 * Math.abs(s.dy);
      if (!s.anim) {
        if (go) this.select(j, false);
        return;
      }

      this.follow(s);
      const cur = this.panels[s.i];
      const next = this.panels[s.peek];
      if (go) {
        // Switch from the normal state (so the no-jump handling in select() works as for a tap), then put the old
        // panel back on top, where the finger left it, to slide out
        const from = [cur.style.transform, next.style.transform];
        this.settle(cur, next);
        this.select(j, false);
        cur.hidden = false;
        cur.classList.add('is-peek');
        cur.style.transform = from[0];
        next.style.transform = from[1];
      }
      const x = go ? -dir * s.w : 0;
      this.sliding = true;
      Promise.all([this.glide(cur, x), next && this.glide(next, x + dir * s.w)]).then(() => {
        this.sliding = false;
        this.box.classList.remove('is-drag');
        this.settle(cur, next);
      });
    }

    // The open panel moves with the finger and the neighbour on that side comes in beside it (is-peek, over the box).
    // At the first / last tab there is nothing to bring in, so the panel only gives a little.
    follow(s) {
      const dir = s.dx < 0 ? 1 : -1;
      const j = this.panels[s.i + dir] ? s.i + dir : -1;
      if (j !== s.peek) {
        this.settle(this.panels[s.peek]);
        s.peek = j;
        if (j >= 0) {
          this.panels[j].hidden = false;
          this.panels[j].classList.add('is-peek');
        }
      }
      const x = j >= 0 ? s.dx : s.dx / 3;
      this.panels[s.i].style.transform = `translateX(${x}px)`;
      if (j >= 0) this.panels[j].style.transform = `translateX(${x + dir * s.w}px)`;
    }

    // Back to normal: no offset, not on top, shown only if its tab is the selected one
    settle(...panels) {
      panels.forEach((p) => {
        if (!p) return;
        p.classList.remove('is-peek');
        p.style.removeProperty('transform');
        p.hidden = this.tabs[this.panels.indexOf(p)].getAttribute('aria-selected') !== 'true';
      });
    }

    // Short slide from where the panel is now to x. The end value is set inline first, so nothing jumps when the
    // animation ends; without Web Animations the panel just goes there. The timer is a safety net in case the end
    // is never reported (the animation keeps playing over the inline value until it is done anyway).
    glide(p, x) {
      const from = p.style.transform || 'none';
      p.style.transform = `translateX(${x}px)`;
      return new Promise((done) => {
        setTimeout(done, SLIDE + 100);
        if (!p.animate) return done();
        const a = p.animate([{ transform: from }, { transform: p.style.transform }], { duration: SLIDE, easing: 'cubic-bezier(.22, .61, .36, 1)' });
        a.onfinish = a.oncancel = done;
      });
    }

    // Something inside a panel that scrolls sideways keeps its own swipe (there is nothing like that today)
    scrolls(el) {
      for (; el && el !== this.box; el = el.parentElement) {
        if (el.scrollWidth > el.clientWidth + 1 && /auto|scroll/.test(getComputedStyle(el).overflowX)) return true;
      }
      return false;
    }
  }

  customElements.define('dp-community-tabs', DpCommunityTabs);
})();
